import { createSlice } from "@reduxjs/toolkit";

import { type TTenantState } from "../../types/tenant.type";
import {
	getActiveTenant,
	getPreferredActiveTenant,
	getTenantIdFromPathname,
	setActiveTenant,
} from "../../utils/tenantUtils";

import {
	getAllAvailableTenantThunk,
	getAllTenantsForUser,
	getAllUsersInTenant,
} from "./tenant.thunk";

const initialState: TTenantState = {
	allTenants: null,
	activeTenant: getTenantIdFromPathname() ?? getActiveTenant(),
	allUsersInTenant: [],
	usersRequestId: null,
	loading: false,
	error: null,
	allAvailableTenants: {
		data: [],
		loading: false,
		error: null,
	},
};

export const tenantSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setActiveTenantReducer: (state, action) => {
			state.activeTenant = action.payload;
			state.allUsersInTenant = [];
			state.usersRequestId = null;
			state.loading = false;
			setActiveTenant(action.payload as string);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAllTenantsForUser.pending, (state) => {
				state.loading = true;
			})
			.addCase(
				getAllTenantsForUser.fulfilled,
				(state, action) => {
					state.allTenants = action.payload;
					const previousActiveTenant =
						getTenantIdFromPathname() ?? state.activeTenant ?? getActiveTenant();
					const allTenantKeys = Object.keys(action.payload);

					const tenantToActivate =
						getPreferredActiveTenant(
							allTenantKeys,
							previousActiveTenant
						);
					if (state.activeTenant !== tenantToActivate) {
						state.allUsersInTenant = [];
						state.usersRequestId = null;
					}

					if (tenantToActivate) {
						setActiveTenant(tenantToActivate);
						state.activeTenant = tenantToActivate;
						state.loading = false;
					} else {
						state.activeTenant = null;
						state.loading = false;
					}
				}
			)
			.addCase(
				getAllTenantsForUser.rejected,
				(state, action) => {
					state.loading = false;
					state.error = action.error as string;
				}
			);
		builder
			.addCase(getAllUsersInTenant.pending, (state, action) => {
				if (action.meta.arg !== state.activeTenant) return;
				state.usersRequestId = action.meta.requestId;
				state.allUsersInTenant = [];
				state.loading = true;
			})
			.addCase(
				getAllUsersInTenant.fulfilled,
				(state, action) => {
					if (
						state.usersRequestId !== action.meta.requestId ||
						state.activeTenant !== action.meta.arg
					) return;
					state.allUsersInTenant = action.payload;
					state.loading = false;
				}
			)
			.addCase(
				getAllUsersInTenant.rejected,
				(state, action) => {
					if (
						state.usersRequestId !== action.meta.requestId ||
						state.activeTenant !== action.meta.arg
					) return;
					state.loading = false;
					state.error = action.error as string;
				}
			);
		builder
			.addCase(
				getAllAvailableTenantThunk.pending,
				(state) => {
					state.allAvailableTenants.loading = true;
				}
			)
			.addCase(
				getAllAvailableTenantThunk.fulfilled,
				(state, action) => {
					const allTenants = Object.values(action.payload);

					state.allAvailableTenants.data = allTenants;
					state.allAvailableTenants.loading = false;
				}
			)
			.addCase(
				getAllAvailableTenantThunk.rejected,
				(state, action) => {
					state.allAvailableTenants.loading = false;
					state.allAvailableTenants.error =
						action.error as string;
				}
			);
	},
});

export const TenantActions = tenantSlice.actions;
export const TenantReducer = tenantSlice.reducer;
